import React from 'react';
import './Home.css';

interface PartnerLandingPageProps {
  onPartnerSignup: () => void;
}

export const PartnerLandingPage: React.FC<PartnerLandingPageProps> = ({ onPartnerSignup }) => {
  return (
    <>
      {/* HERO / PRIMARY CTA */}
      <section className="demo-section">
        <div className="demo-card demo-card-hero">
          <h1 className="hero-title">
            Reduce business entropy for your clients.
          </h1>
          <p className="hero-subtitle" style={{ fontSize: '1.25rem', marginBottom: '0.5rem' }}>
            Don't just advise—help them execute.
          </p>

          <p className="hero-subtitle">
            Krysalis improves coordination between intent and execution, giving your clients a way to turn decisions into coordinated action—without more meetings, reporting, or follow-ups.
          </p>
          <p className="hero-subtitle hero-subtitle-secondary">
            Krysalis is a control system for business execution.
            Partners use it to move clients from insight to action faster.
          </p>

          <div className="hero-ctas">
            <button className="demo-start-button" onClick={onPartnerSignup}>
              Become a Partner
            </button>
          </div>

          <div className="hero-fineprint">
            Early access • Direct influence • Execution-first
          </div>
        </div>
      </section>

      {/* KEY DIFFERENTIATOR */}
      <div style={{ textAlign: 'center', padding: '2rem 1rem', fontSize: '1.25rem', fontWeight: '600', color: 'var(--text-secondary, #666)' }}>
        Dashboards observe work. Krysalis coordinates it.
      </div>

      <div className="guest-sections">
        {/* THE PARTNER PROBLEM */}
        <section className="info-section-card section-what-is">
          <h2>You already know this problem.</h2>
          <p>
            Your clients:
          </p>
          <ul style={{ marginLeft: '1.5rem', marginBottom: '1rem' }}>
            <li>Understand what they should do</li>
            <li>Agree on priorities in meetings</li>
            <li>Still struggle to make change stick</li>
            <li>Rely on heroics, follow-ups, and persuasion</li>
            <li>Ask for "dashboards" instead of control</li>
          </ul>
          <p>
            <strong>The gap isn't insight. It's execution coordination.</strong>
          </p>
          <p>
            Most tools observe work after it happens. Most frameworks stop at recommendations.
          </p>
          <p>
            <strong>Your value erodes where execution breaks down.</strong>
          </p>
        </section>

        {/* WHAT Krysalis DOES (PARTNER-FRAMED) */}
        <section className="info-section-card section-three-levers">
          <h2>Krysalis closes the execution gap.</h2>
          <p>
            Krysalis reduces business entropy—the loss of coordination between intent and execution.
          </p>
          <p>
            It does this by:
          </p>

          <div className="info-features">
            <div className="info-feature">
              <h3>Control Bridge for Leaders</h3>
              <p>
                Giving leaders a Control Bridge to steer priorities and constraints with immediate effect on execution.
              </p>
            </div>

            <div className="info-feature">
              <h3>Exo Panels for Employees</h3>
              <p>
                Connecting that bridge directly to Exo Panels employees use to do the work—showing exactly what must happen next.
              </p>
            </div>

            <div className="info-feature">
              <h3>Execution Signals</h3>
              <p>
                Turning recommendations into system-generated execution signals that guide action automatically.
              </p>
            </div>

            <div className="info-feature">
              <h3>Automatic Advancement</h3>
              <p>
                Making completion advance the business automatically—no chasing, no status decks, no translation loss.
              </p>
            </div>
          </div>
        </section>

        {/* WHY THIS MATTERS FOR PARTNERS */}
        <section className="info-section-card section-who-for">
          <h2>Krysalis multiplies your impact.</h2>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', marginTop: '1.5rem' }}>
            <div>
              <h3 style={{ marginBottom: '1rem', color: 'var(--text-secondary, #666)' }}>Instead of:</h3>
              <ul style={{ marginLeft: '1.5rem' }}>
                <li>Repeating advice</li>
                <li>Reinforcing plans</li>
                <li>Chasing adoption</li>
                <li>Acting as the glue</li>
              </ul>
            </div>
            <div>
              <h3 style={{ marginBottom: '1rem', color: 'var(--primary-color, #007bff)' }}>You get:</h3>
              <ul style={{ marginLeft: '1.5rem' }}>
                <li>A shared execution language</li>
                <li>A living model of the client's business</li>
                <li>A system that enforces what you helped design</li>
                <li>Guidance that doesn't disappear after the meeting</li>
              </ul>
            </div>
          </div>
          <p style={{ marginTop: '1.5rem', fontSize: '1.1rem', fontWeight: '600' }}>
            Your guidance doesn't disappear after the meeting—it becomes operational.
          </p>
        </section>

        {/* HOW PARTNERS USE Krysalis */}
        <section className="info-section-card section-tools">
          <h2>How partners use Krysalis</h2>

          <div className="tools-grid">
            <div className="tool-item">
              <h3>1. Design (Architect Mode)</h3>
              <p>
                Partners help clients build the company model, define artifacts, states, and transitions, make assumptions explicit, and clarify what "done" actually means. This replaces vague process maps and slide decks.
              </p>
            </div>

            <div className="tool-item">
              <h3>2. Engage Model</h3>
              <p>
                When the model is ready, it's engaged. Execution signals begin flowing, entropy becomes visible, and gaps surface naturally. This is where insight turns into leverage.
              </p>
            </div>

            <div className="tool-item">
              <h3>3. Operate & Improve</h3>
              <p>
                Clients execute daily work via Exo Panels. Leaders steer via the Control Bridge. Partners observe where entropy persists. Structural improvements are applied deliberately. Partners stop diagnosing symptoms and start correcting structure.
              </p>
            </div>
          </div>
        </section>

        {/* WHAT Krysalis IS (AND IS NOT) */}
        <section className="info-section-card section-early-adopters">
          <h2>What Krysalis is (and is not)</h2>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', marginTop: '1.5rem' }}>
            <div>
              <h3 style={{ marginBottom: '1rem', color: 'var(--primary-color, #007bff)' }}>Krysalis IS:</h3>
              <ul style={{ marginLeft: '1.5rem' }}>
                <li>A business execution control system</li>
                <li>A way to make strategy operational</li>
                <li>A coordination multiplier for partners</li>
                <li>A shared language between advice and action</li>
              </ul>
            </div>
            <div>
              <h3 style={{ marginBottom: '1rem', color: 'var(--text-secondary, #666)' }}>Krysalis is NOT:</h3>
              <ul style={{ marginLeft: '1.5rem' }}>
                <li>A dashboard</li>
                <li>A task manager</li>
                <li>A reporting tool</li>
                <li>A replacement for your expertise</li>
              </ul>
            </div>
          </div>
          <p style={{ marginTop: '1.5rem', fontSize: '1.1rem', fontWeight: '600' }}>
            Krysalis doesn't compete with partners—it operationalizes them.
          </p>
        </section>

        {/* WHO THIS IS FOR (PARTNER EDITION) */}
        <section className="info-section-card section-next">
          <h2>Who this is for</h2>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', marginTop: '1.5rem' }}>
            <div>
              <h3 style={{ marginBottom: '1rem', color: 'var(--primary-color, #007bff)' }}>Krysalis works best for partners who:</h3>
              <ul style={{ marginLeft: '1.5rem' }}>
                <li>Work with growing or complex organizations</li>
                <li>See execution as the real constraint</li>
                <li>Want repeatable, scalable engagement models</li>
                <li>Are tired of advice evaporating after delivery</li>
                <li>Want to stay involved after recommendations are made</li>
              </ul>
            </div>
            <div>
              <h3 style={{ marginBottom: '1rem', color: 'var(--text-secondary, #666)' }}>Krysalis is probably not a fit if:</h3>
              <ul style={{ marginLeft: '1.5rem' }}>
                <li>Your work stops at analysis</li>
                <li>You prefer static deliverables</li>
                <li>You don't engage during execution</li>
              </ul>
            </div>
          </div>
        </section>

        {/* EARLY PARTNER POSITIONING */}
        <section className="info-section-card section-testimonials">
          <h2>Why partner now?</h2>
          <p>
            Krysalis is early—but the core control system is operational.
          </p>
          <p>
            <strong>Early partners:</strong>
          </p>
          <ul style={{ marginLeft: '1.5rem', marginBottom: '1rem' }}>
            <li>Influence product direction</li>
            <li>Shape execution patterns</li>
            <li>Help define how coordination is modeled</li>
            <li>Build differentiated offerings on top of Krysalis</li>
          </ul>
          <p>
            Later partners adopt the platform. Early partners help define it.
          </p>
          <p style={{ marginTop: '1rem', fontStyle: 'italic' }}>
            We are intentionally prioritizing: real execution problems, partner-led feedback, and structural capability over polish.
          </p>
        </section>

        {/* PARTNER VALUE SUMMARY */}
        <section className="info-section-card section-final-cta">
          <h2>Partner value summary</h2>
          <p>
            With Krysalis, partners can:
          </p>
          <ul style={{ marginLeft: '1.5rem', marginTop: '1rem', marginBottom: '1.5rem' }}>
            <li>Shorten time from recommendation to result</li>
            <li>Reduce reliance on meetings and follow-ups</li>
            <li>Make guidance durable instead of aspirational</li>
            <li>Scale impact without scaling effort</li>
            <li>Anchor engagements in execution, not opinion</li>
          </ul>
        </section>

        {/* FINAL CTA */}
        <section className="info-section-card section-final-cta">
          <h2>Ready to help clients execute—not just understand?</h2>
          <p>
            Become a Krysalis partner and gain early access to a system designed to turn insight into coordinated action.
          </p>

          <div className="cta-row">
            <button className="demo-start-button" onClick={onPartnerSignup}>
              Become a Partner
            </button>
          </div>

          <div className="hero-fineprint" style={{ marginTop: '1rem' }}>
            Early access • Execution-first • Influence included
          </div>
        </section>
      </div>
    </>
  );
};
